/** @format */

import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import moment from 'moment';
import i18next from 'i18next';
import { ExportToCsv } from 'export-to-csv';
import { DateHelperTs } from '../../helpers/DateHelperTs';
import { InvoiceCollectionInterface, InvoiceInterface } from '../../interfaces/platform/InvoiceInterface';
import { CurrencyHelper } from '../../helpers/CurrencyHelper';

const _ = require('underscore');

interface InvoiceToCsvData {
    type: string | null;
    soldOn: string | null;
    soldFor: string | null;
    invoiceNumber: string | null;
    seller: string | null;
    buyer: string | null;
    agreement: string | null;
    paymentDeadline: string | null;
    paymentDeadlineClause: string | null;
    amount: string | null;
    taxPercentage: string | null;
    taxAmount: string | null;
    noTaxAmount: string | null;
    paidOn: string | null;
    accepted: string | null;
    deleted: string | null;
    hasPivot: string | null;
    hasIaf: string | null;
}

export class InvoiceToCsv {
    private readonly itemLoader: ItemLoaders;

    constructor(itemLoader: ItemLoaders) {
        this.itemLoader = itemLoader;
    }

    exportToCsv = (orders: Array<Order>, filters: Array<Filter>, showMainLoader: (show: boolean) => void): void => {
        const extendFilter = _.clone(filters);
        extendFilter.push({ name: 'pagination', value: 'false' });
        showMainLoader(true);
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/invoices')),
            'invoice_loader_items',
            extendFilter,
            orders,
            (items: InvoiceCollectionInterface): void => {
                this.prepareCsv(items);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    prepareCsv = (items: InvoiceCollectionInterface): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'invoiceReport' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'Invoice Report',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('documents:invoices.invoice_type'),
                i18next.t('documents:invoices.sold_on'),
                i18next.t('documents:invoices.sold_for'),
                i18next.t('documents:invoices.invoice_number'),
                i18next.t('documents:invoices.seller'),
                i18next.t('documents:invoices.buyer'),
                i18next.t('documents:invoices.agreement'),
                i18next.t('documents:invoices.payment_deadline'),
                i18next.t('documents:invoices.payment_deadline_clause'),
                i18next.t('documents:invoices.amount'),
                i18next.t('documents:invoices.tax_percentage'),
                i18next.t('documents:invoices.tax_amount'),
                i18next.t('documents:invoices.no_tax_amount'),
                i18next.t('documents:invoices.paid_on'),
                i18next.t('documents:invoices.accepted'),
                i18next.t('documents:invoices.deleted'),
                i18next.t('documents:invoices.pivot'),
                i18next.t('documents:invoices.iaf'),
            ],
        };
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvList);
    };

    prepareCsvData = (items: InvoiceCollectionInterface): Array<InvoiceToCsvData> => {
        const csvList: Array<InvoiceToCsvData> = [];
        if (items['hydra:member'] && items['hydra:totalItems'] && items['hydra:totalItems'] > 0) {
            items['hydra:member'].map((invoice: InvoiceInterface) => {
                const paymentDeadlineClause =
                    invoice.paymentDeadlineClause && !invoice.paymentDeadlineClause.includes('DD') && invoice.paymentDeadline
                        ? DateHelperTs.format(invoice.paymentDeadline, 'YYYY-MM-DD')
                        : '';
                const paidOn = invoice.paidOn ? DateHelperTs.format(invoice.paidOn) : '';
                csvList.push({
                    type: i18next.t('documents:invoices.type.' + invoice.type),
                    soldOn: DateHelperTs.format(invoice.soldOn),
                    soldFor: DateHelperTs.format(invoice.soldFor, 'MMMM') + ' ' + DateHelperTs.format(invoice.soldFor, 'YYYY'),
                    invoiceNumber: invoice.invoiceNumber,
                    seller: invoice.seller?.mainname || '',
                    buyer: invoice.buyer?.mainname || '',
                    agreement: invoice.agreement,
                    paymentDeadline: paymentDeadlineClause,
                    paymentDeadlineClause: invoice.paymentDeadlineClause,
                    amount: CurrencyHelper.numericToStandardPln(invoice.amount),
                    taxPercentage: invoice.taxPercentage + '%',
                    taxAmount: CurrencyHelper.numericToStandardPln(invoice.taxAmount),
                    noTaxAmount: CurrencyHelper.numericToStandardPln(invoice.noTaxAmount),
                    paidOn: paidOn,
                    accepted: invoice.accepted ? i18next.t('general:common.yes') : i18next.t('general:common.no'),
                    deleted: invoice.deleted ? i18next.t('general:common.yes') : i18next.t('general:common.no'),
                    hasPivot: invoice.pivotId ? i18next.t('general:common.yes') : i18next.t('general:common.no'),
                    hasIaf: invoice.iafId ? i18next.t('general:common.yes') : i18next.t('general:common.no'),
                });
            });
        }
        return csvList;
    };
}
